// Model sekce hodnocení v patičce (recenze U2): řádky platforem, souhrn a výběr
// citací. Čisté funkce bez DOM a sítě: kreslí je reviews.js, chování drží
// tools/unit/reviews-model.test.mjs.
//
// Rozhodnutí Tomáše 6. 10. 2026 (mock na plátně H8uCHzSqpDc7pGLKVKyswp, řada
// „Patička · sekce hodnocení (U2)", plán ~/.claude/plans/recenze-web.md):
//   - řádek platformy ukazuje její NATIVNÍ metriku (Heureka a Zboží.cz procenta,
//     ostatní hvězdy); na hvězdy se procenta převádějí jen v souhrnu a souhrn
//     to říká větou pod zdrojovým řádkem;
//   - součet počítá šablona z FINÁLNÍCH řádků: `total` z `/reviews/summary`
//     Heureku nezná (je z automatu vyřazená, čísla ručně v manifestu) a záloha
//     z manifestu musí dát totéž číslo jako živá data;
//   - Firmy.cz (`pool`) přebírají hodnocení Zboží.cz: řádek ano, součet ne;
//     Jipos.cz (`own`) jen hodnocení napsaná na webu (importy Heureky počítá
//     řádek Heureky);
//   - citace: tři, nejdřív ověřené Heurekou, pak ostatní; bez minusů, emoji
//     a textů nad 200 znaků (přeskočit, nezkracovat: text je doslovný).

/** Kolik citací se kreslí (tři sloupce na desktopu, pod sebou na mobilu). */
export const QUOTES_SHOWN = 3;

/** Nejdelší citace v patičce (znaky). Delší se přeskakuje, nezkracuje. */
export const QUOTE_MAX_LENGTH = 200;

/** Témata stránek, která zná `/reviews/context` (search/indexer/reviews.mjs CONTEXT_PAGES). */
export const TOPICS = ['obecne', 'doprava', 'reklamace', 'komunikace'];

/** Kolik kandidátů citací chtít od API (víc než tři: filtr část přeskočí). */
export const CONTEXT_LIMIT = 12;

// Emoji podle bloků (cíl buildu es2018, `\p{Extended_Pictographic}` až ES2019):
// piktogramy a symboly U+1F000–U+1FAFF, různé symboly a dingbaty U+2600–U+27BF.
const EMOJI = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}]/u;

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** „4,7": jedno desetinné místo s čárkou (cs i sk). */
export function decimal(value) {
  return (Math.round(value * 10) / 10).toFixed(1).replace('.', ',');
}

/** „2026-10-06" → „6. 10. 2026"; jiný tvar = null. */
export function formatDate(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? Number(m[3]) + '. ' + Number(m[2]) + '. ' + m[1] : null;
}

// ---------------------------------------------------------------------------
// Platformy a souhrn
// ---------------------------------------------------------------------------

/** Hvězdy řádku: procenta lineárně / 20 (96 % = 4,8), hvězdy jak jsou; jinak null. */
export function starsOf(row) {
  if (!row || typeof row.value !== 'number' || !isFinite(row.value)) return null;
  return row.metric === 'pct' ? row.value / 20 : row.value;
}

/**
 * Řádek `/reviews/summary` pro platformu `key`, který smí přepsat zálohu: stav
 * `ok` i `held` (job drží poslední přijatou hodnotu za branou), metrika, kterou
 * šablona umí, hodnota v rozsahu a kladný celočíselný počet. Jinak null.
 */
function apiRow(summary, key) {
  const list = summary && Array.isArray(summary.platforms) ? summary.platforms : [];
  let row = null;
  for (let i = 0; i < list.length; i++) {
    if (list[i] && list[i].id === key) row = list[i];
  }
  if (!row || (row.status !== 'ok' && row.status !== 'held')) return null;
  const max = row.metric === 'pct' ? 100 : row.metric === 'stars' ? 5 : 0;
  if (!(typeof row.value === 'number' && row.value > 0 && row.value <= max)) return null;
  if (!(Number.isInteger(row.count) && row.count > 0)) return null;
  return row;
}

/**
 * Řádky platforem v pořadí manifestu (`reviews.platforms`). Platforma, kterou
 * nese API, dostane jeho metriku, hodnotu a počet (`source: 'api'`), ostatní
 * zálohu z manifestu (`source: 'manifest'`). Platformu mimo manifest API
 * nepřidá: logo, odkaz, pořadí a `pool` rozhoduje šablona.
 */
export function platformRows(platforms, summary) {
  return (platforms || []).map(function (p) {
    const api = apiRow(summary, p.key);
    const data = api ? { metric: api.metric, value: api.value, count: api.count, source: 'api' } : { source: 'manifest' };
    return Object.assign({}, p, data);
  });
}

/**
 * Souhrn řádků bez `pool`: vážený průměr hvězd (procenta převedená) a počet.
 * `names` = platformy v součtu (zdrojový řádek), `pct` = řádky s procenty
 * v součtu (věta o převodu), `converted` = v součtu je převedené procento.
 * Bez jediného řádku s čísly null (sekce se nekreslí).
 */
export function summarize(rows) {
  let count = 0;
  let weighted = 0;
  const names = [];
  const pct = [];
  (rows || []).forEach(function (r) {
    if (r.pool) return;
    const s = starsOf(r);
    if (s === null || !(r.count > 0)) return;
    count += r.count;
    weighted += s * r.count;
    names.push(r.name);
    if (r.metric === 'pct') pct.push(r);
  });
  if (!count) return null;
  return { count: count, stars: weighted / count, names: names, pct: pct, converted: pct.length > 0 };
}

/** Procento řádku Heureky (dlaždice „Ověřený obchod" v pokladně), nebo null. */
export function heurekaPercent(platforms) {
  const list = platforms || [];
  for (let i = 0; i < list.length; i++) {
    if (list[i].key === 'heureka' && list[i].metric === 'pct') return Math.round(list[i].value);
  }
  return null;
}

// ---------------------------------------------------------------------------
// Citace
// ---------------------------------------------------------------------------

function hasText(s) {
  return typeof s === 'string' && s.trim() !== '';
}

/**
 * Text citace: souhrn recenze, jinak body Heureky spojené „ · " (stejně jako
 * citát u názvu na detailu, reviewHighlights.js `buildQuote`).
 */
export function quoteText(q) {
  if (!q || typeof q !== 'object') return '';
  if (hasText(q.text)) return q.text.trim();
  const pros = Array.isArray(q.pros) ? q.pros.filter(hasText) : [];
  return pros.map((p) => p.trim()).join(' · ');
}

/**
 * Citace do patičky: má text, NEMÁ minusy (kladný bod vytržený z recenze
 * s výhradou by zkresloval), bez emoji a nejvýš QUOTE_MAX_LENGTH znaků.
 */
export function footerQuote(q) {
  if (!q || typeof q !== 'object') return false;
  if (Array.isArray(q.cons) && q.cons.some(hasText)) return false;
  const text = quoteText(q);
  return text !== '' && text.length <= QUOTE_MAX_LENGTH && !EMOJI.test(text);
}

/**
 * Nejvýš `n` citací (výchozí QUOTES_SHOWN): nejdřív ověřené Heurekou
 * (rozhodnutí Tomáše 6. 10.), pak ostatní, ve skupině pořadí vstupu (kontext
 * obchodu = nejnovější napřed, kategorie = nejprodávanější produkty napřed).
 * Shodný text jen jednou; `taken` = texty, které řádek už nese.
 */
export function pickQuotes(items, n, taken) {
  const seen = new Set(taken || []);
  const ok = [];
  (Array.isArray(items) ? items : []).forEach(function (q) {
    if (!footerQuote(q)) return;
    const text = quoteText(q);
    if (seen.has(text)) return;
    seen.add(text);
    ok.push(q);
  });
  const verified = ok.filter((q) => q.source === 'heureka');
  const rest = ok.filter((q) => q.source !== 'heureka');
  return verified.concat(rest).slice(0, n == null ? QUOTES_SHOWN : n);
}

/**
 * O čem jsou citace na stránce: výpis kategorie (`pageType` category s GUID)
 * = recenze produktů kategorie, stránka z `topicPages` (cesta → téma, např.
 * Doprava a platba → doprava) = téma, jinak obecné hodnocení obchodu.
 * Název kategorie = poslední článek `category.path` dataLayeru („Zahrada | Pily").
 */
export function pageTopic(path, shoptet, topicPages) {
  const sd = shoptet || {};
  const cat = sd.category;
  if (sd.pageType === 'category' && cat && GUID.test(String(cat.guid || ''))) {
    const parts = String(cat.path || '').split('|').map((s) => s.trim()).filter(Boolean);
    return { topic: 'kategorie', category: { guid: cat.guid, name: parts.length ? parts[parts.length - 1] : null } };
  }
  const map = topicPages || {};
  const topic = Object.prototype.hasOwnProperty.call(map, path) ? map[path] : 'obecne';
  return { topic: TOPICS.indexOf(topic) >= 0 ? topic : 'obecne', category: null };
}

/**
 * Řádek citací. `primary` = citace tématu stránky nebo kategorie, `general` =
 * obecné citace obchodu na doplnění, `fallback` = záložní citace z manifestu
 * (API nic použitelného nevrátilo). Vrací `{title, category, quotes, source}`:
 * `title` = klíč nadpisu (téma, `kategorie`, nebo `obecne`, když se doplňovalo
 * nebo platí záloha), `source` = `api` | `manifest`.
 */
export function quotePlan(input) {
  const o = input || {};
  const topic = o.topic || 'obecne';
  const main = pickQuotes(o.primary);
  const fill = main.length < QUOTES_SHOWN ? pickQuotes(o.general, QUOTES_SHOWN - main.length, main.map(quoteText)) : [];
  const quotes = main.concat(fill);
  if (!quotes.length) {
    return { title: 'obecne', category: null, quotes: pickQuotes(o.fallback), source: 'manifest' };
  }
  const pure = fill.length === 0;
  return { title: pure ? topic : 'obecne', category: pure ? o.category || null : null, quotes: quotes, source: 'api' };
}
